// @steered AudibleSecurityContext 1.2 2026-09-15
import { ConfigStatus } from '../components/ConfigChecklist';

const CLIENT_ID_TO_PAGE_TYPE: Record<string, string> = {
  'AmazonHomesteadDP': 'amazon-dp',
  'AudiblePDP': 'pdp',
  'AudibleListView': 'list-view',
};

/**
 * Normalizes raw page type strings from LLM output to internal format.
 * Handles variations like "Amazon DP", "AmazonDP", "amazon dp", "amazon-dp", "PDP", etc.
 */
function normalizeRawPageType(raw: string): string {
  const lower = raw.toLowerCase().replace(/[\s_]+/g, '-');
  if (lower === 'amazon-dp' || lower === 'amazondp' || lower === 'amazon-detail-page') {
    return 'amazon-dp';
  }
  if (lower === 'pdp' || lower === 'audiblepdp' || lower === 'audible-pdp') {
    return 'pdp';
  }
  if (lower === 'list-view' || lower === 'listview' || lower === 'audiblelistview' || lower === 'audible-list-view') {
    return 'list-view';
  }
  return raw;
}

function derivePageType(clientId?: string, pageType?: string): string | null {
  if (clientId && CLIENT_ID_TO_PAGE_TYPE[clientId]) {
    return CLIENT_ID_TO_PAGE_TYPE[clientId];
  }
  if (pageType) {
    return normalizeRawPageType(pageType);
  }
  return null;
}

/**
 * Parses the structured config block from the orchestrator's response.
 * Handles multiple formats the LLM might output:
 * - ```config\n{...}\n```  (proper markdown)
 * - `config{...}`  (inline, backticks may be stripped)
 * - config\n{...}  (no backticks at all)
 */
export function parseConfigFromResponse(
  responseText: string,
  currentConfig: ConfigStatus
): { config: ConfigStatus; cleanedResponse: string } {
  // Try multiple patterns from most specific to least
  // Use [\s\S] to match nested braces and multiline content
  const patterns = [
    /```config\s*\n?\s*(\{[\s\S]*?\})\s*\n?\s*```/,          // proper markdown fenced
    /`config\s*(\{[\s\S]*?\})\s*`/,                           // inline backticks
    /config\s*\n(\{[\s\S]*?\})\s*(?:\n|$)/,                   // config on its own line
    /config\s*(\{[^}]*(?:\{[^}]*\}[^}]*)*\})/,               // handles one level of nesting
  ];

  let match: RegExpMatchArray | null = null;
  let matchedPattern: RegExp | null = null;

  for (const pattern of patterns) {
    match = responseText.match(pattern);
    if (match) {
      matchedPattern = pattern;
      break;
    }
  }

  if (!match || !matchedPattern) {
    return { config: currentConfig, cleanedResponse: responseText };
  }

  try {
    // Parse the config block which may have unquoted keys but always has quoted values from the LLM.
    // Use a simple tokenizer approach since regex-based fixing breaks on values containing colons.
    let jsonStr = match[1].trim();
    
    // Strategy: The LLM outputs {key: "value", key2: "value2"} format.
    // Keys are unquoted identifiers, values are always quoted strings (or null/true/false/numbers).
    // We need to add quotes around keys while preserving quoted values intact.
    const fixedParts: string[] = [];
    let i = 0;
    
    while (i < jsonStr.length) {
      const ch = jsonStr[i];
      
      if (ch === '{' || ch === '}' || ch === ',' || ch === ':') {
        fixedParts.push(ch);
        i++;
      } else if (ch === '"') {
        // Quoted string - consume until closing quote (handle escaped quotes)
        let str = '"';
        i++;
        while (i < jsonStr.length) {
          if (jsonStr[i] === '\\' && i + 1 < jsonStr.length) {
            str += jsonStr[i] + jsonStr[i + 1];
            i += 2;
          } else if (jsonStr[i] === '"') {
            str += '"';
            i++;
            break;
          } else {
            str += jsonStr[i];
            i++;
          }
        }
        fixedParts.push(str);
      } else if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
        // Skip whitespace
        i++;
      } else {
        // Unquoted token (key name or unquoted value like null/true/false/number)
        let token = '';
        while (i < jsonStr.length && !/[{},:"'\s]/.test(jsonStr[i])) {
          token += jsonStr[i];
          i++;
        }
        // Quote it as a JSON string (works for both keys and unquoted values)
        if (token === 'null' || token === 'true' || token === 'false' || /^\d+(\.\d+)?$/.test(token)) {
          fixedParts.push(token);
        } else {
          fixedParts.push(`"${token}"`);
        }
      }
    }
    
    jsonStr = fixedParts.join('');

    const parsed = JSON.parse(jsonStr);
    const newConfig: ConfigStatus = {
      asin: parsed.asin || currentConfig.asin,
      asinType: parsed.asinType || currentConfig.asinType,
      customerType: parsed.customerType || currentConfig.customerType,
      marketplace: parsed.marketplace || currentConfig.marketplace,
      pageType: derivePageType(parsed.clientId, parsed.pageType) || currentConfig.pageType,
      weblabTreatment: parsed.weblabTreatment || currentConfig.weblabTreatment,
      // Keep the exact clientId: it decides which preview surfaces (web / native) apply.
      clientId: (typeof parsed.clientId === 'string' && parsed.clientId.trim()) || currentConfig.clientId,
      applicationType: (typeof parsed.applicationType === 'string' && parsed.applicationType.trim()) || currentConfig.applicationType || null,
    };

    // Strip the config block from the displayed response
    const cleanedResponse = responseText.replace(matchedPattern, '').trim();

    return { config: newConfig, cleanedResponse };
  } catch (e) {
    console.error('[parseConfigFromResponse] Failed to parse config block:', e, 'Raw:', match[1]);
    // Still strip the config block even if parsing fails
    const cleanedResponse = responseText.replace(matchedPattern!, '').trim();
    return { config: currentConfig, cleanedResponse };
  }
}
